import { highlight, languages } from 'prismjs';
import { useState } from 'react';
import { FiMoreVertical } from 'react-icons/fi';
import Editor from 'react-simple-code-editor';
import { toast } from 'react-toastify';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';

import APIGateway from '@/api';
import { Banner } from '@/components/banner/banner';
import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { useUpdateSourceVersion } from '@/hooks/queries/useSources';
import {
  Source,
  SOURCE_TYPES,
  SourceConfig,
  SourceCreateReq,
  SourcePreview,
  SourceUpdateReq,
  SourceVersionDeprecateReq
} from '@/types/evals';

import ModalSourceCreate from '../../_modalSourceCreate/modalSourceCreate';
import { ConfigBlock } from './ConfigBlocks';
import { DescriptionBlock } from './DescriptionBlock';
import { EmptyLogsAlert } from './EmptyLogsAlert';
import { FooterRow } from './FooterRow';
import { MetaRow } from './MetaRow';
import styles from './VersionConfigurationTab.module.scss';

const LEARN_MORE_URL = 'https://kanpai-docs.kanpai.gaai-labs.com/agent_features/logging_and_observability';

type Props = {
  source: Source;
  preview: SourcePreview | undefined;
  previewIsLoading: boolean;
  onSourceChanged: ( newVersion?: number ) => void;
  onVersionChange: ( newVersion: number ) => void;
};

const LOG_SOURCE_TYPES: ReadonlySet<string> = new Set( [
  SOURCE_TYPES.COMPLETION_LOGS,
  SOURCE_TYPES.AGENT_ACTION_LOGS,
  SOURCE_TYPES.MODEL_INVOCATION_LOGS
] );

function PreviewComponent( {
  preview,
  showEmptyLogsAlert
}: {
  preview: SourcePreview | undefined;
  showEmptyLogsAlert: boolean;
} ) {
  return (
    <section className={ styles.previewBlock } data-testid="data-sample-preview">
      <h3 className={ styles.previewHeading }>Data Sample Preview</h3>
      { showEmptyLogsAlert ? (
        <EmptyLogsAlert />
      ) : (
        <div className={ styles.previewList }>
          { preview?.items.map( el => (
            <div className={ styles.previewItem } key={ `preview-${ el.id }` }>
              <Editor
                value={ JSON.stringify( el, undefined, 2 ) }
                onValueChange={ () => null }
                highlight={ ( code ) => highlight( code, languages.json, 'json' ) }
                padding={ 10 }
                readOnly
                style={ {
                  fontFamily: '"Fira code", "Fira Mono", monospace',
                  fontSize: 12
                } }
              />
            </div>
          ) ) }
        </div>
      ) }
    </section>
  );
}

export function VersionConfigurationTab( { source, preview, previewIsLoading, onSourceChanged, onVersionChange }: Props ) {
  const updateSourceVersionMutation = useUpdateSourceVersion();

  const [ isDeprecating, setIsDeprecating ] = useState( false );
  const [ showModalSource, setShowModalSource ] = useState( false );
  const [ isCreatingNewVersion, setIsCreatingNewVersion ] = useState( false );

  const toggleDeprecation = async() => {
    setIsDeprecating( true );
    try {
      const depReq: SourceVersionDeprecateReq = { is_deprecated: !source.is_deprecated };
      await APIGateway.evals.sourceVersionDeprecate( source.id, source.version, depReq );
      toast( `source version ${ depReq.is_deprecated ? 'deprecated' : 'undeprecated' }` );
      onSourceChanged();
    } catch( e ) {
      console.error( e );
      toast( 'unable to update deprecation status' );
    } finally {
      setIsDeprecating( false );
    }
  };

  const onCreateNewSourceVersion = async( args: SourceCreateReq ) => {
    setIsCreatingNewVersion( true );
    try {
      // JSON sources version on top-level examples; all others on config.
      const data: SourceUpdateReq = source.source_type === SOURCE_TYPES.JSON
        ? ( { examples: ( args as unknown as { examples: unknown[] } ).examples } as unknown as SourceUpdateReq )
        : { config: args.config as SourceConfig };

      const res = await updateSourceVersionMutation.mutateAsync( {
        sourceId: source.id,
        data
      } );
      setShowModalSource( false );
      onVersionChange( res.version );
      toast( `New source version (${ res.version }) created` );
    } catch( e ) {
      console.error( e );
      const errorMessage = e instanceof Error ? e.message : 'Unable to create new source version';
      toast( errorMessage );
    } finally {
      setIsCreatingNewVersion( false );
    }
  };

  const versionLabel = source.is_deprecated
    ? `Version ${ source.version } (Deprecated)`
    : `Version ${ source.version }`;

  const isLogSource = LOG_SOURCE_TYPES.has( source.source_type );
  const hasNoLogs = !previewIsLoading && ( !preview || preview.items.length === 0 );
  const showEmptyLogs = isLogSource && hasNoLogs;
  // The backend collapses every failure mode (connection error, empty data, etc.)
  // into status='error'. For log sources, an empty source also reports 'error', so
  // showing the connection-error banner there would be wrong — we show the empty-logs
  // banner instead. We therefore only show the error banner for non-log sources.
  // Future work (see docs/source-error-reason.md): once the backend persists an
  // explicit status_reason, branch on that instead of source type so we can show the
  // right banner even for a log source that has a genuine connection error.
  const showErrorBanner = source.status === 'error' && !isLogSource;
  // A log source with no logs is red only when it's genuinely errored; a healthy
  // source that simply hasn't received data yet shows a neutral "logs will appear" note.
  const emptyLogsVariant = source.status === 'error' ? 'error' : 'info';

  return (
    <div
      className={ styles.tab }
      data-deprecated={ source.is_deprecated ? 'true' : undefined }
    >
      {
        showModalSource && (
          <ModalSourceCreate
            cbAction={ onCreateNewSourceVersion }
            onCloseAction={ () => setShowModalSource( false ) }
            sourceToEdit={ source }
            isPending={ isCreatingNewVersion }
          />
        )
      }

      <div className={ styles.topBar }>
        <div className={ styles.topBarLeft }>
          <span className={ styles.topBarHeading }>Configuration Properties for</span>
          <Menu
            variant="select"
            value={ versionLabel }
            className={ styles.versionSelector }
            data-testid="version-selector"
          >
            { Array.from( { length: source.latest_version_number ?? 0 }, ( _, i ) => i + 1 ).map( ( v ) => (
              <MenuItem
                key={ `version-option-${ v }` }
                value={ `Version ${ v }` }
                onClick={ () => onVersionChange( v ) }
                data-testid={ `version-option-${ v }` }
              />
            ) ) }
          </Menu>
        </div>
        <div className={ styles.topBarRight }>
          <Button
            variant="primary"
            onClick={ () => setShowModalSource( true ) }
            disabled={ isCreatingNewVersion }
            data-testid="new-source-version-button"
          >
            { isCreatingNewVersion ? 'Creating...' : 'Version Source' }
          </Button>
          <Menu
            variant="menu"
            icon={ <Icon icon={ FiMoreVertical } /> }
            data-testid="version-kebab"
          >
            <MenuItem
              value={ source.is_deprecated ? 'Undeprecate Version' : 'Deprecate Version' }
              onClick={ toggleDeprecation }
              disabled={ isDeprecating }
              data-testid="version-deprecate-toggle"
            />
          </Menu>
        </div>
      </div>

      { source.is_deprecated && (
        <Banner variant="warning" data-testid="deprecated-banner">
          Version { source.version } of <strong>&ldquo;{ source.name }&rdquo;</strong> is deprecated
        </Banner>
      ) }

      { showErrorBanner && (
        <Banner variant="error" data-testid="source-error-banner">
          Version { source.version } of <strong>&ldquo;{ source.name }&rdquo;</strong> had a connection error. Reconnect or check again later.
        </Banner>
      ) }

      { showEmptyLogs && (
        <Banner variant={ emptyLogsVariant } data-testid="empty-logs-banner">
          Logs will appear once your agents or requests start running.
          { ' ' }
          <a
            href={ LEARN_MORE_URL }
            target="_blank"
            rel="noopener noreferrer"
            className={ styles.bannerLink }
          >
            Learn More
          </a>
        </Banner>
      ) }

      <MetaRow source={ source } />

      <DescriptionBlock description={ source.description } />

      <ConfigBlock source={ source } />

      <PreviewComponent preview={ preview } showEmptyLogsAlert={ showEmptyLogs } />

      <FooterRow source={ source } />
    </div>
  );
}
